import {
  Divider,
  Skeleton,
  Stack,
  SvgIconProps,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { addMonths, format, startOfDay, startOfMonth } from 'date-fns';

type Props = {
  icon: React.FC<SvgIconProps>;
  label: string;
  isLoading?: boolean;
} & (
  | {
      type: 'monthly';
      remaining: number;
      total: number;
      surplus?: number;
    }
  | {
      type: 'fixed';
      credits: number;
    }
);

function MarketingCreditsItem({
  icon: Icon,
  label,
  isLoading,
  ...props
}: Props) {
  const theme = useTheme();
  const nextMonthFirstDay = startOfDay(startOfMonth(addMonths(new Date(), 1)));
  const textColor = 'text.primary';

  return (
    <Tooltip
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -5],
              },
            },
          ],
        },
      }}
      arrow
      title={
        isLoading
          ? ''
          : props.type === 'monthly'
          ? `You have ${props.remaining} ${label} credits remaining out of ${
              props.total
            }. Your credits will reset on ${format(
              nextMonthFirstDay,
              'MMMM d, yyyy',
            )}.${
              props.surplus
                ? ` You have a credit surplus of ${props.surplus} that will be carried over to the next month in addition to your monthly credits.`
                : ''
            }`
          : `You have ${props.credits} ${label} credits. As an employee of a shop, you must purchase credits to start campaigns. Credits can be purchased at the time of campaign creation.`
      }
      placement="top">
      <Stack
        color={textColor}
        direction="row"
        alignItems="center"
        gap={0.5}
        borderRadius="4px"
        bgcolor={theme.palette.lightGrey.main}
        p={1}>
        <Icon
          htmlColor="inherit"
          sx={{
            fontSize: 12,
          }}
        />
        <Typography variant="overline" component="p" color="inherit">
          {label}
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {!isLoading && (
          <Typography variant="body2" color="inherit">
            {props.type === 'monthly'
              ? `${props.remaining} of ${props.total}`
              : `${props.credits} credits`}
          </Typography>
        )}
        {isLoading && (
          <Skeleton
            variant="text"
            width={75}
            height={14}
            sx={{ borderRadius: '4px' }}
          />
        )}
      </Stack>
    </Tooltip>
  );
}

export default MarketingCreditsItem;
